import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Download, Smartphone, Star } from "lucide-react";
import { APK_URL, APK_FILENAME } from "@/lib/constants";
import localApi, { LOCAL_API } from "@/lib/localApi";

const SESSION_KEY = "nd_dl_banner_dismissed";
const DELAY_MS = 4000;

export default function DownloadBanner() {
  const [visible, setVisible] = useState(false);
  const [href, setHref] = useState(APK_URL);

  useEffect(() => {
    if (sessionStorage.getItem(SESSION_KEY)) return;
    const t = setTimeout(() => setVisible(true), DELAY_MS);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    let alive = true;
    localApi
      .get("/app/version")
      .then(({ data }) => {
        if (alive && data?.exists) setHref(`${LOCAL_API}/app/download`);
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, []);

  const dismiss = () => {
    sessionStorage.setItem(SESSION_KEY, "1");
    setVisible(false);
  };

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          data-testid="download-banner"
          initial={{ opacity: 0, y: 40, scale: 0.96 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 40, scale: 0.96 }}
          transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
          className="fixed z-[60] bottom-4 left-4 right-4 sm:right-auto sm:w-[380px]"
        >
          <div className="relative bg-card border border-border rounded-3xl shadow-[0_24px_60px_-20px_rgba(42,36,30,0.45)] p-5 grain overflow-hidden">
            <button
              data-testid="download-banner-dismiss"
              onClick={dismiss}
              aria-label="Dismiss"
              className="absolute top-3 right-3 grid place-items-center w-8 h-8 rounded-full hover:bg-secondary transition-colors"
            >
              <X className="w-4 h-4" />
            </button>

            <div className="flex items-start gap-4">
              <span className="grid place-items-center w-14 h-14 rounded-2xl bg-primary text-primary-foreground arch shrink-0">
                <Smartphone className="w-7 h-7" />
              </span>
              <div className="min-w-0 pr-4">
                <p className="font-display font-extrabold text-lg tracking-tight leading-tight">
                  Get the NearbyDeals app
                </p>
                <div className="flex items-center gap-1 text-primary text-sm font-semibold mt-0.5">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-3.5 h-3.5 fill-primary" />
                  ))}
                  <span className="text-muted-foreground font-medium ml-1">Offers &amp; coupons on the go</span>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-2 mt-4">
              <a
                href={href}
                download={APK_FILENAME}
                data-testid="download-banner-cta"
                onClick={dismiss}
                className="flex-1 inline-flex items-center justify-center gap-2 rounded-full bg-primary text-primary-foreground font-semibold py-2.5 hover:bg-primary/90 transition-colors"
              >
                <Download className="w-4 h-4" /> Download for Android
              </a>
              <button
                data-testid="download-banner-later"
                onClick={dismiss}
                className="rounded-full border border-border font-semibold px-4 py-2.5 text-sm hover:border-primary hover:text-primary transition-colors"
              >
                Later
              </button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
